/**
 * Mega Menu
 * Handles panel open/close, sub-sector drill-down, and burger toggle
 */

export function initMegaMenu() {
    const panels = document.querySelectorAll('.mega-panel');
    const body = document.body;
    const masthead = document.querySelector('.masthead');
    const logoLink = masthead ? masthead.querySelector('a[data-logo]') : null;
    const logoImg = logoLink ? logoLink.querySelector('img') : null;
    const logoLight = logoImg ? logoImg.getAttribute('src') : '';
    const logoColour = logoLink ? logoLink.getAttribute('data-logo') : '';

    if (!panels.length) return;

    // ─── Masthead solid state (scroll + mega open) ───
    let megaIsOpen = false;

    function updateMastheadState() {
        const scrolled = window.scrollY > 50;
        const shouldBeSolid = scrolled || megaIsOpen;

        if (shouldBeSolid) {
            masthead.classList.add('masthead--solid');
            masthead.classList.remove('bg--transparent');
            if (logoImg) logoImg.src = logoColour;
        } else {
            masthead.classList.remove('masthead--solid');
            masthead.classList.add('bg--transparent');
            if (logoImg) logoImg.src = logoLight;
        }
    }

    window.addEventListener('scroll', updateMastheadState, { passive: true });
    updateMastheadState();

    // ─── Nav item click → toggle panel ───
    // Generic: any header menu item with children and a matching panel.
    // Panels are rendered with keys like: data-mega-panel="menu-item-<ID>"
    const navItems = document.querySelectorAll('.masthead .menu > .menu-item');
    navItems.forEach((item) => {
        if (!item.classList.contains('menu-item-has-children')) return;

        // Find the WordPress menu item id from classes like "menu-item-123"
        const idClass = Array.from(item.classList).find((c) => /^menu-item-\d+$/.test(c));
        if (!idClass) return;

        const panelKey = idClass; // "menu-item-123"
        const panel = document.querySelector(`[data-mega-panel="${panelKey}"]`);
        if (!panel) return;

        const link = item.querySelector(':scope > a');
        if (!link) return;

        // Prevent navigation, use as trigger
        link.setAttribute('data-mega-key', panelKey);
        link.addEventListener('click', (e) => {
            e.preventDefault();
            togglePanel(panelKey);
        });

        // Add chevron indicator styling hook
        item.classList.add('has-mega');
    });

    // ─── Burger trigger ───
    const burgerTrigger = document.querySelector('[data-mega-trigger="burger"]');
    const tabletBreakpoint = 1024;

    if (burgerTrigger) {
        burgerTrigger.addEventListener('click', () => {
            const panelKey = window.innerWidth < tabletBreakpoint ? 'mobile' : 'burger';
            togglePanel(panelKey);
        });
    }

    // ─── Toggle panel logic ───
    function togglePanel(key) {
        const target = document.querySelector(`[data-mega-panel="${key}"]`);
        if (!target) return;

        const isOpen = target.classList.contains('is-open');

        // Close all panels first
        closeAllPanels();

        if (!isOpen) {
            target.classList.add('is-open');
            body.classList.add('mega-open');
            megaIsOpen = true;
            updateMastheadState();

            // Mark the triggering nav item as active
            const activeNavItem = document.querySelector(`.has-mega a[data-mega-key="${key}"]`);
            if (activeNavItem) activeNavItem.closest('.has-mega').classList.add('is-active');

            // Reset to the panel's main view
            const mainView = target.querySelector('.mega-panel__view--main[data-mega-view]');
            if (mainView) showView(target, mainView.getAttribute('data-mega-view'));

            // Toggle burger icon
            updateBurgerIcon(key === 'burger' || key === 'mobile');
        } else {
            updateBurgerIcon(false);
        }
    }

    function closeAllPanels() {
        panels.forEach((p) => p.classList.remove('is-open'));
        body.classList.remove('mega-open');
        megaIsOpen = false;
        updateMastheadState();
        updateBurgerIcon(false);

        // Remove active state from all nav items
        document.querySelectorAll('.has-mega.is-active').forEach((el) => el.classList.remove('is-active'));

        // Reset mobile nav to main page
        const mobileNavEl = document.querySelector('.mobile-nav');
        if (mobileNavEl) {
            mobileNavEl.querySelectorAll('.mobile-nav__page').forEach((p) => p.classList.remove('is-active'));
            const mainPage = mobileNavEl.querySelector('.mobile-nav__page--main');
            if (mainPage) mainPage.classList.add('is-active');
        }
    }

    function updateBurgerIcon(isOpen) {
        if (!burgerTrigger) return;
        const iconOpen = burgerTrigger.querySelector('.masthead__burger-icon');
        const iconClose = burgerTrigger.querySelector('.masthead__burger-close');
        if (iconOpen) iconOpen.style.display = isOpen ? 'none' : '';
        if (iconClose) iconClose.style.display = isOpen ? '' : 'none';
    }

    // ─── Sub-sector drill-down ───
    const sectorBtns = document.querySelectorAll('.mega-panel__term-btn[data-sector-id]');
    sectorBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
            const sectorId = btn.dataset.sectorId;
            const panel = btn.closest('.mega-panel');
            showView(panel, `sector-${sectorId}`);
        });
    });

    // ─── Back buttons ───
    const backBtns = document.querySelectorAll('.mega-panel__back[data-mega-back]');
    backBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
            const targetView = btn.dataset.megaBack;
            const panel = btn.closest('.mega-panel');
            showView(panel, targetView);
        });
    });

    // ─── Burger sub-panel switching (auto-detect from wp_nav_menu) ───
    const burgerSubpanels = document.querySelectorAll('.mega-panel__burger-subpanel[data-burger-content]');
    const burgerNavItems = document.querySelectorAll('.mega-panel__page-list .menu-item');
    const activeTriggers = [];

    // Build a map of available panel keys
    const panelKeys = new Set();
    burgerSubpanels.forEach((sp) => panelKeys.add(sp.dataset.burgerContent));

    burgerNavItems.forEach((item) => {
        // WordPress adds classes like 'menu-item-object-page' and slug-based classes
        // Try to match item CSS classes against available panel keys
        const classes = Array.from(item.classList);
        let matchedKey = null;

        panelKeys.forEach((key) => {
            // Match by slug pattern: the class will contain the page slug
            // WordPress generates classes like 'menu-item-123' and page slug classes
            const slugClass = classes.find((c) => c.includes(key));
            if (slugClass) matchedKey = key;
        });

        // Also try matching by link text (fallback)
        if (!matchedKey) {
            const link = item.querySelector('a');
            if (link) {
                // Strip non-alphanumeric chars (& etc.) then convert spaces to hyphens
                const text = link.textContent.trim().toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/\s+/g, '-');
                if (panelKeys.has(text)) matchedKey = text;
                // Partial match
                if (!matchedKey) {
                    panelKeys.forEach((key) => {
                        if (text.includes(key) || key.includes(text)) matchedKey = key;
                    });
                }
            }
        }

        if (!matchedKey) return;

        const link = item.querySelector('a');
        if (!link) return;

        // Intercept click to switch panel
        link.addEventListener('click', (e) => {
            e.preventDefault();

            // Toggle active on all triggers
            activeTriggers.forEach((t) => t.item.classList.remove('is-active'));
            item.classList.add('is-active');

            // Toggle sub-panels
            burgerSubpanels.forEach((sp) => sp.classList.remove('is-active'));
            const target = document.querySelector(`[data-burger-content="${matchedKey}"]`);
            if (target) target.classList.add('is-active');
        });

        activeTriggers.push({ item, key: matchedKey });
        item.classList.add('has-burger-panel');

        // Add chevron icon to the link
        const chevron = document.createElement('span');
        chevron.classList.add('mega-panel__term-chevron');
        chevron.innerHTML = '<svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        link.appendChild(chevron);
    });

    // Set active trigger to match the default active sub-panel
    const defaultPanel = document.querySelector('.mega-panel__burger-subpanel.is-active');
    if (defaultPanel) {
        const defaultKey = defaultPanel.dataset.burgerContent;
        const match = activeTriggers.find((t) => t.key === defaultKey);
        if (match) match.item.classList.add('is-active');
    }

    // ─── View switching ───
    function showView(panel, viewKey) {
        const views = panel.querySelectorAll('.mega-panel__view');
        views.forEach((v) => v.classList.remove('is-active'));

        const target = panel.querySelector(`[data-mega-view="${viewKey}"]`);
        if (target) target.classList.add('is-active');
    }

    // ─── Close on ESC ───
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAllPanels();
    });

    // ─── Close on outside click ───
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.mega-panel') &&
            !e.target.closest('.masthead__burger-btn') &&
            !e.target.closest('.has-mega')) {
            closeAllPanels();
        }
    });

    // ─── Mobile nav: page switching ───
    const mobileNav = document.querySelector('.mobile-nav');
    if (mobileNav) {
        const mobilePages = mobileNav.querySelectorAll('.mobile-nav__page');

        // Forward: go to sub-page
        mobileNav.querySelectorAll('[data-mobile-target]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const targetKey = btn.dataset.mobileTarget;
                const targetPage = mobileNav.querySelector(`[data-mobile-page="${targetKey}"]`);
                if (!targetPage) return;

                mobilePages.forEach((p) => p.classList.remove('is-active'));
                targetPage.style.animation = 'none';
                targetPage.offsetHeight; // force reflow
                targetPage.style.animation = '';
                targetPage.classList.add('is-active');
            });
        });

        // Back: return to main
        mobileNav.querySelectorAll('[data-mobile-back]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const mainPage = mobileNav.querySelector('.mobile-nav__page--main');
                if (!mainPage) return;

                mobilePages.forEach((p) => p.classList.remove('is-active'));
                mainPage.style.animation = 'none';
                mainPage.offsetHeight; // force reflow
                mainPage.style.animation = 'mobileSlideBack 300ms ease forwards';
                mainPage.classList.add('is-active');
            });
        });
    }
}
